import React, { lazy, Suspense, useEffect, useMemo } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";
import { ReactComponent as PlusIcon } from "@common/assets/images/shared-icons/plus-icon-v2.svg";
import { FeedbackPreviewDialogBody } from "@common/components/Dialog/configurations/FeedbackPreviewDialogBody";
import { SpaceToPreviewDialog } from "@common/components/Dialog/SpaceToPreviewDialog";
import { ErrorStateView } from "@common/components/PageStateView/components/ErrorStateView";
import PageWrap from "@common/components/PageWrap/PageWrap";
import { useTableInterjections } from "@common/components/ShakeTable/ui/interjectedRows/useTableInterjections";
import { Spinner } from "@common/components/Spinner/Spinner";
import { useFeedbackAttributes } from "@common/hooks/filtering/modules/useFeedbackAttributes";
import { Attributes } from "@common/hooks/filtering/ui/Attributes";
import { PlatformName } from "@common/models";
import { isFreePlan } from "@common/util/workspaceUtil";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { FeedbackModuleAddAppFooter } from "@main/pages/shared/components/AppFooter/AddAppFooter/AddAppFooter";
import { FeedbackModuleChromeAppFooter } from "@main/pages/shared/components/AppFooter/ChromeAppFooter/ChromeAppFooter";
import { FeedbackModuleFreePlanFooter } from "@main/pages/shared/components/AppFooter/FreePlanFooter/FreePlanFooter";
import { EmptyStateViewConf, EmptyTableView } from "@main/pages/shared/components/EmptyTableView/EmptyTableView";
import { FeedbackModuleEmptyState } from "@main/pages/shared/components/ModuleEmptyState/FeedbackModuleEmptyState";
import { SdkSetupSteps } from "@main/pages/shared/components/SDKSetup/SdkSetupSteps";
import { resolveEmptyStateView } from "@main/pages/shared/helpers/resolveEmptyStateView";
import useUserFeedbackApiConsumer from "@main/pages/UserFeedback/consumers/useUserFeedbackApiConsumer";
import { UserFeedbackAddNewTicketModal } from "@main/pages/UserFeedback/views/UserFeedbackAddNewTicketView/UserFeedbackAddNewTicketModal";
import { useUserFeedbackAddNewTicketModal } from "@main/pages/UserFeedback/views/UserFeedbackAddNewTicketView/useUserFeedbackAddNewTicketModal";

import * as Styled from "./UserFeedback.styles";
import useSearchFieldsApiConsumer from "../consumers/useSearchFieldsApiConsumer";

const Table = lazy(() =>
  import("@common/components/ShakeTable/BaseTable").then((module) => ({ default: module.BaseTable })),
);

const renderLoader = () => <Spinner />;

export const UserFeedbackListPageView: React.FC = () => {
  const { selectedApp, selectedWorkspace } = useAppSelectionContext();
  const { sendEvent } = useAnalytics();

  useEffect(() => {
    sendEvent("page_view", { page_title: "User feedback list" });
  }, [sendEvent]);

  const { customFields, metadataFields } = useSearchFieldsApiConsumer();

  // Attributes filter logic
  const feedbackAttributeState = useFeedbackAttributes(false, metadataFields, customFields);

  const { items, loading, hasMore, total, error, refetchData, feedbackTableProps, spaceToPreviewDialogProps } =
    useUserFeedbackApiConsumer({
      attributesProps: feedbackAttributeState,
    });

  const hasFilter = Boolean(feedbackAttributeState.validAttributes.length);

  const ticketDates = useMemo(() => items?.map((item) => item.original.issue_reported_time) ?? [], [items]);

  const interjections = useTableInterjections("feedback", { hasActiveFilters: hasFilter, total, ticketDates });
  const isError = Boolean(error);

  const addNewTicketModal = useUserFeedbackAddNewTicketModal({ refetchData });

  useEffect(() => {
    if (feedbackAttributeState.validAttributes) {
      feedbackTableProps.table.resetRowSelection();
    }
    // eslint-disable-next-line
  }, [feedbackAttributeState.validAttributes]);

  if (!selectedApp?.is_approved) {
    return <SdkSetupSteps />;
  }

  if (!loading && !isError && !total && !hasFilter) return <FeedbackModuleEmptyState />;

  if (error && !hasFilter) {
    return (
      <ErrorStateView
        title="There was an error loading tickets. Try refreshing."
        subtitle="The link you followed may be broken, or the workspace may have been removed."
      />
    );
  }

  const hasItems = (items && items.length > 0) || total > 0;

  const emptyViewConfig = resolveEmptyStateView(
    loading,
    "feedback",
    hasItems,
    selectedApp,
    hasFilter,
    Boolean(error),
    refetchData,
  ) as EmptyStateViewConf | undefined;

  const buildAppFooter = () => {
    if (Boolean(selectedApp?.is_sample)) return <FeedbackModuleAddAppFooter />;
    if (Boolean(selectedApp?.is_extension) && selectedApp?.title === "My First Project" && items && items.length < 2)
      return <FeedbackModuleChromeAppFooter />;
    if (isFreePlan(selectedWorkspace) && !Boolean(hasMore) && selectedApp.platform.name !== PlatformName.WEB)
      return <FeedbackModuleFreePlanFooter />;
    return null;
  };

  return (
    <>
      <PageWrap style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Styled.Toolbar>
          <Styled.NewTicketButton
            style={{ marginRight: "0.8rem" }}
            onClick={() => addNewTicketModal.setAddNewTicketModalActive(true)}
          >
            {"New ticket"}
            <PlusIcon />
          </Styled.NewTicketButton>
          <Attributes
            {...feedbackAttributeState}
            searchField="title"
            enableKeyboardShortcut={true}
          />
        </Styled.Toolbar>

        {emptyViewConfig && (
          <EmptyTableView
            withFilters
            {...emptyViewConfig}
          />
        )}
        {loading && <Spinner />}
        {!emptyViewConfig && !loading && (
          <Suspense fallback={renderLoader()}>
            {
              // eslint-disable-next-line
              // @ts-ignore
              <Table
                {...feedbackTableProps}
                interjections={interjections}
                footer={buildAppFooter()}
                isFetchingNext={feedbackTableProps.isFetchingNext}
              />
            }
          </Suspense>
        )}
      </PageWrap>
      <SpaceToPreviewDialog {...spaceToPreviewDialogProps}>
        <FeedbackPreviewDialogBody />
      </SpaceToPreviewDialog>
      {addNewTicketModal.active ? <UserFeedbackAddNewTicketModal {...addNewTicketModal} /> : null}
    </>
  );
};
